
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { safeExtractString, asString } from '@/utils/supabase-helpers';

interface StudioDetail {
  id: string;
  name: string;
  description: string;
  logo_url?: string;
  banner_url?: string;
  location: {
    address: string;
    city: string;
    postal_code: string;
    country: string;
    latitude?: number;
    longitude?: number;
    phone?: string;
  };
  opening_hours: Record<string, { open: string; close: string }>;
  is_featured: boolean;
  rating: number;
  services: any[];
  staff: any[];
}

export const useStudioBySlug = (slug: string | undefined) => {
  const [studio, setStudio] = useState<StudioDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    const fetchStudio = async () => {
      if (!slug) {
        setLoading(false);
        setError('No studio slug provided');
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Fetch the studio by slug using type assertion to bypass TS errors
        const { data: studioData, error: studioError } = await supabase
          .from('studios')
          .select('*')
          .eq('slug', slug as string)
          .single();

        if (studioError) {
          throw studioError;
        }

        if (!studioData) {
          setError('Studio not found');
          setLoading(false);
          return;
        }
        
        // Handle geo_location safely
        let location = {
          address: 'Keine Adresse verfügbar',
          city: 'Berlin',
          postal_code: '10115',
          country: 'Germany',
          latitude: 52.52,
          longitude: 13.405,
          phone: studioData?.phone as string || undefined
        };
        
        try {
          if (studioData && typeof studioData === 'object' && 'geo_location' in studioData && studioData.geo_location) {
            const geoData = studioData.geo_location as any;
            if (geoData) {
              location = {
                address: safeExtractString(geoData, 'address', 'Keine Adresse verfügbar'),
                city: safeExtractString(geoData, 'city', 'Berlin'),
                postal_code: safeExtractString(geoData, 'postal_code', '10115'),
                country: safeExtractString(geoData, 'country', 'Germany'),
                latitude: geoData.coordinates?.latitude || 52.52,
                longitude: geoData.coordinates?.longitude || 13.405,
                phone: studioData?.phone as string || undefined
              };
            }
          }
        } catch (err) {
          console.error('Error parsing geo location data:', err);
        }

        // Fetch services for the studio using type assertion
        const studioId = studioData ? safeExtractString(studioData, 'id', '') : '';

        // Mock services data since our database doesn't have the services table
        const servicesData: any[] = [];

        // Mock staff members for the studio
        const staffData: any[] = [];

        // Construct the complete studio object with null checks
        const studioDetail: StudioDetail = {
          id: studioData ? safeExtractString(studioData, 'id', '') : '',
          name: studioData ? safeExtractString(studioData, 'name', '') : '',
          description: studioData ? safeExtractString(studioData, 'description', '') : '',
          logo_url: studioData ? safeExtractString(studioData, 'logo_url', '') : '',
          banner_url: studioData ? safeExtractString(studioData, 'banner_url', '') : '',
          location,
          opening_hours: studioData && typeof studioData === 'object' && 'opening_hours' in studioData ? 
            (studioData.opening_hours as Record<string, { open: string; close: string }> || {}) : {},
          is_featured: studioData && typeof studioData === 'object' && 'is_featured' in studioData ? Boolean(studioData.is_featured) : false,
          rating: studioData && typeof studioData === 'object' && 'rating' in studioData ? Number(studioData.rating || 0) : 0,
          services: servicesData || [],
          staff: staffData || []
        };

        setStudio(studioDetail);
      } catch (err: any) {
        console.error('Error in useStudioBySlug:', err);
        setError(err.message || 'Failed to fetch studio');
        toast({
          title: 'Fehler',
          description: 'Studio konnte nicht geladen werden.',
          variant: 'destructive',
        });
      } finally {
        setLoading(false);
      }
    };

    fetchStudio();
  }, [slug, toast]);

  return { studio, loading, error };
};

// Also export as default for backward compatibility
export default useStudioBySlug;
